/**
 * Assessment — provider mount for TV-P5-07, migrated to @collabkare/sdc-react (SDC-INT-2).
 *
 * Renders each curated assessment tab through <SdcForm>. Catalog-backed
 * instruments (PHQ-9, GAD-7, PRAPARE, AUDIT-C) use the bundled, self-hosted
 * Questionnaire + scoring/flag metadata from @collabkare/sdc-catalog — no
 * HAPI round-trip needed to render them. Tabs without a bundled catalog
 * instrument (Morse Fall) fall back to loading the FHIR Questionnaire from
 * the server, same as before, and render through the same <SdcForm>.
 *
 * On submit:
 *   1. @collabkare/sdc's extractObservations(questionnaire, qr, {scoreCode})
 *      derives a total-score Observation for scored catalog instruments
 *      (LOINC total-score codes — same codes the old extractObservations.js
 *      used). Catalog instrument items aren't LOINC item-coded, so per-item
 *      Observations are not emitted (deviation from the old renderer — see
 *      SDC-INT-2 migration notes).
 *   2. useSaveQuestionnaireResponse() — POST QR + Observations as one FHIR
 *      transaction Bundle (encounter-linked; HAPI-1094 handled inside the
 *      hook via withEncounter, unchanged).
 *   3. Surface success / error inline; on success, reset so the provider can
 *      re-take or move to the next tab.
 */

import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '@/components';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { useSessionStore } from '@/stores/sessionStore';
import { usePatient, useQuestionnaire, useSaveQuestionnaireResponse } from '@/fhir/hooks';
import { FhirErrorBanner } from '@/fhir/errors';
import { extractObservations } from '@collabkare/sdc';
import { catalogInstruments } from '@collabkare/sdc-catalog';
import { SdcForm } from '@collabkare/sdc-react';

// ── Curated tab list ──────────────────────────────────────────────────────
//
// Surface order in the Assess tab. `catalogId` maps to a bundled
// @collabkare/sdc-catalog instrument (self-hosted, no FHIR round-trip to
// render). `questionnaireId` is used when there's no bundled instrument —
// the Questionnaire is loaded from the tenant's FHIR server instead (see
// KeyCloak/televisit-seed-questionnaires.sh for Morse Fall's seed).
const ASSESSMENT_TABS = [
  { id: 'phq-9', label: 'PHQ-9', catalogId: 'phq-9', scoreCode: { system: 'http://loinc.org', code: '44261-6', display: 'Patient Health Questionnaire 9 item (PHQ-9) total score' } },
  { id: 'gad-7', label: 'GAD-7', catalogId: 'gad-7', scoreCode: { system: 'http://loinc.org', code: '70274-6', display: 'Generalized anxiety disorder 7 item (GAD-7) total score' } },
  { id: 'prapare', label: 'PRAPARE', catalogId: 'prapare' },
  { id: 'audit-c', label: 'AUDIT-C', catalogId: 'audit-c', scoreCode: { system: 'http://loinc.org', code: '75624-7', display: 'AUDIT-C Total score' } },
  { id: 'morse-fall-scale', label: 'Morse Fall', questionnaireId: 'morse-fall-scale' },
];

const catalogById = new Map(catalogInstruments.map((i) => [i.id, i]));

export default function Assessment() {
  const { encounterId } = useParams();
  const fhirPatientId = useSessionStore(
    (s) => s.currentPatient || s.smartContext?.patientId,
  );
  const [activeId, setActiveId] = useState(ASSESSMENT_TABS[0].id);
  const [savedSummary, setSavedSummary] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [formKey, setFormKey] = useState(0);

  const activeTab = ASSESSMENT_TABS.find((t) => t.id === activeId) || ASSESSMENT_TABS[0];
  const instrument = activeTab.catalogId ? catalogById.get(activeTab.catalogId) : undefined;

  // Subject Patient resource — feeds SdcForm's launchContext for prepopulation.
  const {
    data: patientResource,
    isLoading: patientLoading,
    error: patientError,
  } = usePatient(fhirPatientId);

  // Non-catalog tabs (Morse Fall) still load their Questionnaire from FHIR.
  const { data: serverQuestionnaire, isLoading: qLoading, error: qError } = useQuestionnaire(
    activeTab.questionnaireId,
  );

  const questionnaire = instrument ? instrument.questionnaire : serverQuestionnaire;

  const saveMutation = useSaveQuestionnaireResponse();

  const launchContext = useMemo(() => {
    if (!fhirPatientId) return undefined;
    return {
      patient: patientResource
        ? { resourceType: 'Patient', id: fhirPatientId, ...patientResource }
        : `Patient/${fhirPatientId}`,
      ...(encounterId ? { encounter: `Encounter/${encounterId}` } : {}),
    };
  }, [fhirPatientId, patientResource, encounterId]);

  const handleSubmit = async (qr) => {
    setSaveError(null);
    setSavedSummary(null);
    try {
      const observations = questionnaire
        ? extractObservations(questionnaire, qr, {
            ...(fhirPatientId ? { subject: { reference: `Patient/${fhirPatientId}` } } : {}),
            ...(encounterId ? { encounter: { reference: `Encounter/${encounterId}` } } : {}),
            ...(activeTab.scoreCode ? { scoreCode: activeTab.scoreCode } : {}),
          })
        : [];
      const result = await saveMutation.mutateAsync({ qr, observations });
      setSavedSummary({
        qrId: result.qr?.id || null,
        observationCount: result.observations?.length || 0,
        questionnaireTitle: questionnaire?.title || activeTab.label,
      });
      // Remount the form so the provider gets a clean slate for a re-take.
      setFormKey((k) => k + 1);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Assessment] save failed:', err);
      setSaveError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  const isLoading = activeTab.questionnaireId ? qLoading : false;
  const loadError = activeTab.questionnaireId ? qError : null;

  return (
    <div className="px-6 py-4 space-y-4">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Assessments</h1>
          <p className="text-xs text-slate-400 mt-1">
            FHIR Questionnaires — answers persist to the patient's chart on submit.
          </p>
        </div>
        {!fhirPatientId && (
          <span className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1">
            No patient in context — assessments will load read-only.
          </span>
        )}
      </header>

      {/* Tab strip */}
      <div className="flex flex-wrap gap-2">
        {ASSESSMENT_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveId(tab.id);
              setSavedSummary(null);
              setSaveError(null);
              setFormKey((k) => k + 1);
            }}
            className={`px-3 py-1.5 rounded text-sm border transition-colors ${
              activeId === tab.id
                ? 'bg-blue-600 border-blue-500 text-white'
                : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <div className="p-4 space-y-4">
          {patientError && <FhirErrorBanner error={patientError} />}
          {patientLoading && (
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading patient context…
            </div>
          )}

          {savedSummary && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-3 flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-emerald-200">
                <p className="font-semibold">
                  {savedSummary.questionnaireTitle} saved.
                </p>
                <p className="text-xs text-emerald-200/80 mt-1">
                  {savedSummary.observationCount} Observation
                  {savedSummary.observationCount === 1 ? '' : 's'} written
                  {savedSummary.qrId
                    ? ` · QuestionnaireResponse/${savedSummary.qrId}`
                    : ''}
                  .
                </p>
              </div>
            </div>
          )}

          {saveError && (
            <div className="bg-red-500/10 border border-red-500/30 rounded p-3 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-200">
                <p className="font-semibold">Couldn't save the response.</p>
                <p className="text-xs text-red-200/80 mt-1">
                  {saveError.message || 'Try again in a moment.'}
                </p>
              </div>
            </div>
          )}

          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-slate-400 py-6">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading questionnaire…
            </div>
          )}
          {loadError && <FhirErrorBanner error={loadError} />}

          {!isLoading && !loadError && questionnaire && (
            <div className="rounded-lg bg-white p-4">
              <SdcForm
                key={`${activeTab.id}-${formKey}`}
                questionnaire={questionnaire}
                instrument={instrument}
                launchContext={launchContext}
                density="comfortable"
                onSubmit={handleSubmit}
              />
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
